import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Button, message, Input, Space, Spin, Radio } from 'antd';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../../utils/axiosConfig';
import BatchPDF from './BatchPDF';
import BatchPDF2 from './BatchPDF2';

// Register fonts locally instead of using Google Fonts
Font.register({
  family: 'Roboto',
  fonts: [
    { 
      src: `${window.location.origin}/fonts/Roboto/Roboto-VariableFont_wdth,wght.ttf`,
      fontWeight: 'normal'
    },
    { 
      src: `${window.location.origin}/fonts/Roboto/Roboto-Italic-VariableFont_wdth,wght.ttf`,
      fontStyle: 'italic',
      fontWeight: 'normal'
    }
  ],
});

const ExportPopupDB = ({ open, onCancel, batch, items }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [exportFormat, setExportFormat] = useState('pdf');

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/dropdown/mcategories');
      setCategories(response.data);
      setSelectedCategories(response.data.map(cat => cat.CategoryName));
    } catch (error) {
      message.error('Failed to fetch categories');
    }
  };

  const handleCategoryChange = (checkedValues) => {
    setSelectedCategories(checkedValues);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCategories(filteredCategories.map(cat => cat.CategoryName));
    } else {
      setSelectedCategories([]);
    }
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const filteredCategories = categories.filter(category =>
    category.CategoryName.toLowerCase().includes(searchText.toLowerCase())
  );

  const prepareExportData = async () => {
    setLoading(true);
    try {
      // Use Promise.all to fetch items in parallel
      const allItemsPromises = items.map(async (indent) => {
        try {
          const response = await axiosInstance.get(`/batch-indents/indents/${indent.IndentID}/items`);
          return response.data.map(item => ({
            ...item,
            LocationName: indent.ProjectLocationName || 'Unassigned',
            ProjectName: indent.ProjectName || 'Unassigned',
            IndentNumber: item.IndentID || indent.IndentID || 'N/A'
          }));
        } catch (error) {
          console.error(`Error fetching items for indent ${indent.IndentID}:`, error);
          return [];
        }
      });

      const allItemsArrays = await Promise.all(allItemsPromises);
      const allItems = allItemsArrays.flat();

      // Group items by location, project, and category in a single pass
      const groupedData = allItems.reduce((acc, item) => {
        const location = item.LocationName || 'Unassigned';
        const project = item.ProjectName || 'Unassigned';
        const category = item.CategoryName || 'Unassigned';

        if (!acc[location]) acc[location] = {};
        if (!acc[location][project]) acc[location][project] = {};
        if (!acc[location][project][category]) acc[location][project][category] = [];

        // Only store necessary data
        acc[location][project][category].push({
          ItemName: item.ItemName,
          UnitName: item.UnitName,
          Quantity: item.Quantity,
          Stock: item.Stock,
          CommitedDate: item.CommitedDate,
          Description: item.Description,
          CommitedDays: item.TypeName === 'normal' ? item.NormalDays : item.EmergencyDays,
          NormalDays: item.NormalDays,
          EmergencyDays: item.EmergencyDays,
          IndentNumber: item.IndentNumber || 'N/A'
        });

        return acc;
      }, {});

      setExportData(groupedData);
    } catch (error) {
      console.error('Error preparing export data:', error);
      message.error('Failed to prepare export data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      await prepareExportData();
    } catch (error) {
      console.error('Error preparing export data:', error);
      message.error('Failed to prepare export data');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!exportData) return;

    const workbook = XLSX.utils.book_new();
    const rows = [];
    
    // Add header row with S.No
    rows.push(['S.No', 'Item Name', 'Unit', 'Quantity', 'Stock', 'Committed Date', 'Project', 'Category', 'Location']);
    
    // Process data in chunks to avoid memory issues
    const chunkSize = 1000;
    let currentChunk = [];
    
    Object.entries(exportData).forEach(([location, projects]) => {
      Object.entries(projects).forEach(([project, categories]) => {
        // Reset serial number for each project
        let projectSerialNumber = 1;
        
        Object.entries(categories).forEach(([category, items]) => {
          if (selectedCategories.includes(category)) {
            items.forEach(item => {
              currentChunk.push([
                projectSerialNumber++, // Project-specific serial number
                item.ItemName,
                item.UnitName,
                item.Quantity,
                item.Stock,
                item.CommitedDate ? new Date(item.CommitedDate).toLocaleDateString() : 'N/A',
                project,
                category,
                location
              ]);
              
              if (currentChunk.length >= chunkSize) {
                rows.push(...currentChunk);
                currentChunk = [];
              }
            });
          }
        });
      });
    });
    
    // Add any remaining items
    if (currentChunk.length > 0) {
      rows.push(...currentChunk);
    }
    
    // Create worksheet with data
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    
    // Set column widths for the new header structure
    const colWidths = [
      { wch: 8 },  // S.No
      { wch: 40 }, // Item Name
      { wch: 15 }, // Unit
      { wch: 15 }, // Quantity
      { wch: 15 }, // Stock
      { wch: 20 }, // Committed Date
      { wch: 30 }, // Project
      { wch: 30 }, // Category
      { wch: 30 }  // Location
    ];
    worksheet['!cols'] = colWidths;
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Batch Details');
    
    // Save the workbook
    XLSX.writeFile(workbook, `batch-${batch.BatchNumber}-details.xlsx`);
  };

  const exportToCSV = () => {
    if (!exportData) return;

    let csvContent = 'S.No,Item Name,Unit,Quantity,Stock,Committed Date,Project,Category,Location\n';
    const chunkSize = 1000;
    let currentChunk = '';

    Object.entries(exportData).forEach(([location, projects]) => {
      Object.entries(projects).forEach(([project, categories]) => {
        // Reset serial number for each project
        let projectSerialNumber = 1;
        
        Object.entries(categories).forEach(([category, items]) => {
          if (selectedCategories.includes(category)) {
            items.forEach(item => {
              currentChunk += `${projectSerialNumber++},"${item.ItemName}","${item.UnitName}",${item.Quantity},${item.Stock},"${item.CommitedDate ? new Date(item.CommitedDate).toLocaleDateString() : 'N/A'}","${project}","${category}","${location}"\n`;
              
              if (currentChunk.length >= chunkSize * 100) { // Approximate chunk size
                csvContent += currentChunk;
                currentChunk = '';
              }
            });
          }
        });
      });
    });

    // Add any remaining items
    if (currentChunk) {
      csvContent += currentChunk;
    }

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `batch-${batch.BatchNumber}-details.csv`;
    link.click();
  };

  return (
    <Modal
      title="Export Batch Details"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <div style={{ marginBottom: 20 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Radio.Group 
            value={exportFormat} 
            onChange={(e) => setExportFormat(e.target.value)}
            style={{ marginBottom: 16 }}
          >
            <Radio.Button value="pdf">PDF</Radio.Button>
            <Radio.Button value="excel">Excel</Radio.Button>
            <Radio.Button value="csv">CSV</Radio.Button>
          </Radio.Group>

          <Input
            placeholder="Search categories..."
            value={searchText}
            onChange={handleSearch}
            style={{ marginBottom: 10 }}
          />
          
          <Checkbox
            onChange={(e) => handleSelectAll(e.target.checked)}
            checked={selectedCategories.length === filteredCategories.length}
            indeterminate={
              selectedCategories.length > 0 &&
              selectedCategories.length < filteredCategories.length
            }
          >
            Select All
          </Checkbox>

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Checkbox.Group
              value={selectedCategories}
              onChange={handleCategoryChange}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              {filteredCategories.map(category => (
                <Checkbox key={category.CategoryID} value={category.CategoryName}>
                  {category.CategoryName}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </Space>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleExport}
          loading={loading}
          disabled={selectedCategories.length === 0}
        >
          Prepare Export
        </Button>
      </div>

      {exportData && (
        <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          {exportFormat === 'pdf' && (
            <>
              <PDFDownloadLink
                document={
                  <BatchPDF
                    batch={batch}
                    data={exportData}
                    selectedCategories={selectedCategories}
                  />
                }
                fileName={`batch-${batch.BatchNumber}-category-wise.pdf`}
              >
                {({ blob, url, loading, error }) => (
                  <Button type="primary" loading={loading}>
                    {loading ? 'Preparing PDF...' : 'Download Category-wise PDF'}
                  </Button>
                )}
              </PDFDownloadLink>
              <PDFDownloadLink
                document={
                  <BatchPDF2
                    batch={batch}
                    data={exportData}
                    selectedCategories={selectedCategories}
                  />
                }
                fileName={`batch-${batch.BatchNumber}-location-wise.pdf`}
              >
                {({ blob, url, loading, error }) => (
                  <Button type="primary" loading={loading}>
                    {loading ? 'Preparing PDF...' : 'Download Location-wise PDF'}
                  </Button>
                )}
              </PDFDownloadLink>
            </>
          )}
          {exportFormat === 'excel' && (
            <Button type="primary" onClick={exportToExcel}>
              Download Excel
            </Button>
          )}
          {exportFormat === 'csv' && (
            <Button type="primary" onClick={exportToCSV}>
              Download CSV
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ExportPopupDB;